import React, { useEffect, useRef } from "react";
import { Stage, Layer, Image as KonvaImage, Group } from "react-konva";
import { socket } from "../../io";

const Sorteo = ({
  width = Math.floor(window.visualViewport?.width || window.innerWidth),
  height = Math.floor(window.visualViewport?.height || window.innerHeight),
  file = "",
  onReady,
}) => {
  const layerRef = useRef(null);
  const konvaImageRef = useRef(null);

  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const currentAbsSrcRef = useRef("");
  const readySentRef = useRef(false);

  // ⏱️ pausa al final + espera 30s (sin replay)
  const endTimeoutRef = useRef(null);

  const normalize = (src) => new URL(src, window.location.href).href;

  const clearEndTimeout = () => {
    if (endTimeoutRef.current) {
      clearTimeout(endTimeoutRef.current);
      endTimeoutRef.current = null;
    }
  };

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  };

  const startLoop = () => {
    stopLoop();
    const tick = () => {
      const v = videoRef.current;
      if (!v || v.paused || v.ended) return stopLoop();
      if (layerRef.current) layerRef.current.batchDraw();
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const setSourceIfChanged = (src) => {
    const v = videoRef.current;
    if (!v) return;

    const abs = normalize(src);
    if (currentAbsSrcRef.current === abs) return; // mismo => no reiniciar

    currentAbsSrcRef.current = abs;
    readySentRef.current = false;

    clearEndTimeout();
    stopLoop();

    v.pause();
    v.src = src;
    v.currentTime = 0;
    v.load(); // fuerza canplay
  };

  // Crear video UNA VEZ
  useEffect(() => {
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.loop = false; // ✅ NO loop
    v.preload = "auto";

    const onCanPlay = async () => {
      try {
        // pinta 1 frame para que no quede negro
        if (layerRef.current) layerRef.current.batchDraw();

        await v.play();
        startLoop();

        if (!readySentRef.current) {
          readySentRef.current = true;
          if (typeof onReady === "function") onReady();
        }
      } catch {
        // autoplay bloqueado (poco común si muted)
      }
    };

    const onEnded = () => {
      // ✅ se queda PAUSADO en el último frame
      stopLoop();
      v.pause();
      if (layerRef.current) layerRef.current.batchDraw();

      // ✅ espera 30s (pero NO reproduce)
      clearEndTimeout();
      endTimeoutRef.current = setTimeout(() => {
        // no hacemos nada a propósito
        // quedará pausado hasta que llegue OTRO video (cambio de src)
      }, 30000);
    };

    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("ended", onEnded);

    videoRef.current = v;

    if (konvaImageRef.current) {
      konvaImageRef.current.image(v);
    }

    // carga inicial
    if (file) setSourceIfChanged(`/videos/${file}`);

    return () => {
      clearEndTimeout();
      stopLoop();

      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("ended", onEnded);

      v.pause();
      v.src = "";
      v.load();

      videoRef.current = null;
      currentAbsSrcRef.current = "";
      readySentRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket listener UNA VEZ
  useEffect(() => {
    const onVideo = (vd) => {
      const name = vd?.video || vd; // puede llegar objeto o string
      const next = String(name || "");
      if (!next) return;

      const src =
        next.startsWith("/") || next.startsWith("http")
          ? next
          : `/videos/${next}`;

      setSourceIfChanged(src);
    };

    socket.on("video", onVideo);
    return () => socket.off("video", onVideo);
  }, []);

  return (
    <Stage width={width} height={height}>
      <Layer ref={layerRef}>
        <Group x={0} y={0} width={width} height={height}>
          <KonvaImage
            ref={konvaImageRef}
            x={0}
            y={0}
            width={width}
            height={height}
          />
        </Group>
      </Layer>
    </Stage>
  );
};

export default Sorteo;